import type { GlbHierarchyNode } from '@puppycad/cad-core';

/**
 * 将项目装配删除/恢复快照投影为稳定父节点下的局部设计树更新。
 *
 * 本对象只编排 hierarchy 纯数据，不读取场景 Object3D、不请求 Worker，也不修改模型历史。
 */
export class ProjectModelStructureFeatureTreeProjector<TNodeMeta> {
  /**
   * 创建项目装配结构设计树投影器。
   *
   * @param ports GLB 子树构建与 FeatureTree 提交端口。
   */
  public constructor(
    private readonly ports: ProjectModelStructureFeatureTreeProjectionPorts<TNodeMeta>,
  ) {}

  /**
   * 按稳定父 hierarchy 分组并提交全部结构变化。
   *
   * @param input 删除/恢复主状态已经提交后的 hierarchy 快照。
   * @returns 全部父节点均完成局部投影时返回 true；不具备稳定局部条件时返回 false。
   */
  public apply(input: ProjectModelStructureFeatureTreeProjectionInput): boolean {
    if (
      input.tabId !== input.activeTreeTabId
      || (input.removedSnapshots.length === 0 && input.addedSnapshots.length === 0)
    ) {
      return false;
    }

    const snapshotsByParentObjectId = this.groupSnapshotsByParentObjectId(
      input.removedSnapshots,
      input.addedSnapshots,
    );
    if (!snapshotsByParentObjectId) {
      return false;
    }

    /** 先解析全部父节点，避免后一个父身份缺失时已经提交前一个分组。 */
    const parentHierarchyByObjectId = new Map<string, GlbHierarchyNode>();
    for (const parentObjectId of snapshotsByParentObjectId.keys()) {
      const parentHierarchy = this.findHierarchyNodeByObjectId(
        input.previousHierarchy,
        parentObjectId,
      ) ?? this.findHierarchyNodeByObjectId(input.nextHierarchy, parentObjectId);
      if (!parentHierarchy) {
        return false;
      }
      parentHierarchyByObjectId.set(parentObjectId, parentHierarchy);
    }

    for (const [parentObjectId, snapshots] of snapshotsByParentObjectId) {
      const parentHierarchy = parentHierarchyByObjectId.get(parentObjectId)!;
      const insertionIndexByObjectId = new Map(
        snapshots.added.map(snapshot => [snapshot.node.objectId, snapshot.parentIndex]),
      );
      const update = this.ports.buildRuntimeSubtreeUpdate(
        input.tabId,
        parentHierarchy,
        snapshots.removed.map(snapshot => snapshot.node),
        snapshots.added.map(snapshot => snapshot.node),
        input.fileIdByUuidMap,
        insertionIndexByObjectId,
      );
      if (!this.ports.applyRuntimeSubtreeUpdate(input.tabId, update)) {
        return false;
      }
    }
    return true;
  }

  /**
   * 按稳定父对象 ID 合并删除与恢复快照。
   *
   * @param removedSnapshots 删除快照。
   * @param addedSnapshots 恢复快照。
   * @returns 父节点分组；任一快照缺少父身份时返回 null。
   */
  private groupSnapshotsByParentObjectId(
    removedSnapshots: readonly ProjectModelStructureHierarchySnapshot[],
    addedSnapshots: readonly ProjectModelStructureHierarchySnapshot[],
  ): Map<string, ProjectModelStructureHierarchySnapshotGroup> | null {
    const snapshotsByParentObjectId = new Map<
      string,
      ProjectModelStructureHierarchySnapshotGroup
    >();
    const append = (
      snapshot: ProjectModelStructureHierarchySnapshot,
      kind: 'removed' | 'added',
    ): boolean => {
      const parentObjectId = snapshot.parentObjectId?.trim();
      if (!parentObjectId) {
        return false;
      }
      const group = snapshotsByParentObjectId.get(parentObjectId) ?? {
        removed: [],
        added: [],
      };
      group[kind].push(snapshot);
      snapshotsByParentObjectId.set(parentObjectId, group);
      return true;
    };

    for (const snapshot of removedSnapshots) {
      if (!append(snapshot, 'removed')) return null;
    }
    for (const snapshot of addedSnapshots) {
      if (!append(snapshot, 'added')) return null;
    }
    return snapshotsByParentObjectId;
  }

  /**
   * 按对象 ID 查找 hierarchy 节点。
   *
   * @param root 当前 hierarchy 根。
   * @param objectId 目标对象 ID。
   * @returns 命中的节点；不存在时返回 null。
   */
  private findHierarchyNodeByObjectId(
    root: GlbHierarchyNode,
    objectId: string,
  ): GlbHierarchyNode | null {
    const pending = [root];
    while (pending.length > 0) {
      const node = pending.pop()!;
      if (node.objectId === objectId) {
        return node;
      }
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        pending.push(node.children[index]!);
      }
    }
    return null;
  }
}
